import { Middleware, StoreEnhancer } from 'redux'

import { LOAD_REDUX_STATE, SAVE_REDUX_STATE } from '@/components/App/actionTypes'
import { initialState } from '@/components/AuthenticationHandler/reducer'
import clientSessionStorage from '@/utilities/clientSessionStorage'

import { RootState } from '..'

const STORAGE_KEY = 'redux-cache'

/**
 * Listens for the save Redux state action.
 */
export const middleware: Middleware<{}, RootState> = storeAPI => next => action => {
  if (action.type !== SAVE_REDUX_STATE) return next(action)

  // clone the state tree so we don't mutate it
  const state = structuredClone(storeAPI.getState())
  // don't save expired authentication data
  state.authentication = { ...initialState }

  clientSessionStorage.setItem(STORAGE_KEY, state)

  // no reducers should act on this action so we can safely execute it last as it won't affect the state tree
  return next(action)
}

/**
 * Wraps reducers to intercept actions that need to mutate the entire Redux state tree. This is necessary as standard
 * action reducers can only affect the portion of Redux state they control.
 */
export const enhancer: StoreEnhancer = next => {
  return (reducer, preloadedState) => {
    return next((state, action) => {
      if (action.type !== LOAD_REDUX_STATE) return reducer(state, action)

      const persistedState = clientSessionStorage.getItem(STORAGE_KEY)
      clientSessionStorage.removeItem(STORAGE_KEY)

      if (persistedState === null) return state
      return { ...preloadedState, ...persistedState }
    }, preloadedState)
  }
}
